/**
 * RegionalSalesQr — "QR Scans by Venue" card: per-venue scan bars with
 * conversion column. Port of the V3Sales QR module.
 *
 * Venue names come live from the sales DTO's perVenue list (sibling
 * exposure); scan counts sit behind <MockedOverlay> until BON-138
 * (check widget-telemetry coverage there before building new).
 */
import { MockedOverlay } from '../primitives';

import type { RegionalSalesDto } from './regionalApi';

const EM_DASH = '—';

/** Layout-only skeleton bar widths, longest-first like the design. */
const SKELETON_WIDTHS = [88, 64, 41, 22] as const;

function QrRow({ name, widthPct }: { name: string; widthPct: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        style={{
          width: 84,
          fontSize: 11.5,
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      <div
        style={{
          flex: 1,
          height: 7,
          background: 'var(--nb-surface-2)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${widthPct}%`, height: '100%', background: 'var(--nb-border-2)' }} />
      </div>
      <span
        className="nb-tabular nb-mono"
        style={{ fontSize: 11, color: 'var(--nb-ink)', minWidth: 40, textAlign: 'right' }}
      >
        {EM_DASH}
      </span>
      <span
        className="nb-tabular nb-mono"
        style={{ fontSize: 10.5, color: 'var(--nb-primary)', minWidth: 44, textAlign: 'right' }}
      >
        {EM_DASH}
      </span>
    </div>
  );
}

export function RegionalSalesQr({ perVenue }: { perVenue: RegionalSalesDto['perVenue'] }) {
  const names =
    perVenue.length > 0
      ? perVenue.slice(0, SKELETON_WIDTHS.length).map((v) => v.venueName)
      : SKELETON_WIDTHS.map(() => EM_DASH);

  return (
    <section className="nb-card">
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '0.04em', margin: 0 }}>
          QR Scans by Venue
        </h3>
        <span className="nb-eyebrow">7 days</span>
      </div>
      <MockedOverlay reason="QR scan counts aren't on the sales DTO yet. Tracked in BON-138.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {names.map((name, i) => (
            <QrRow
              key={`${name}-${SKELETON_WIDTHS[i]}`}
              name={name}
              widthPct={SKELETON_WIDTHS[i]}
            />
          ))}
        </div>
      </MockedOverlay>
    </section>
  );
}
